import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LoggerService } from '../services/logger.service';

/**
 * Functional HTTP interceptor that handles all HTTP errors globally.
 *
 * Features:
 * - Logs all HTTP errors
 * - Handles 401 Unauthorized by redirecting to login
 * - Handles 403 Forbidden with appropriate message
 * - Re-throws errors for local handling
 *
 * Registration:
 *   provideHttpClient(withInterceptors([httpErrorInterceptor]))
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const log = inject(LoggerService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Don't log cancelled requests
      if (error.status === 0 && error.statusText === 'Unknown Error') {
        console.debug('Request cancelled or network error:', req.url);
        return throwError(() => error);
      }

      // Log the error
      logError(error, req);

      // Handle specific HTTP status codes
      switch (error.status) {
        case HttpStatusCode.Unauthorized:
          console.warn('Unauthorized access - redirecting to login');
          router.navigate(['/login']);
          break;
        case HttpStatusCode.Forbidden:
          log.displayError('You do not have permission to perform this action.', 'Access Denied');
          break;
        case HttpStatusCode.NotFound:
          console.warn(`Resource not found: ${req.url}`);
          break;
        case HttpStatusCode.InternalServerError:
          log.displayError('An unexpected server error occurred. Please try again later.', 'Server Error');
          break;
        case HttpStatusCode.ServiceUnavailable:
          log.displayError('The service is temporarily unavailable. Please try again later.', 'Service Unavailable');
          break;
      }

      // Re-throw the error for local error handling
      return throwError(() => error);
    })
  );
};

function logError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
  const errorMessage = getErrorMessage(error);

  console.error('HTTP Error:', {
    status: error.status,
    statusText: error.statusText,
    url: request.url,
    method: request.method,
    message: errorMessage,
  });
}

function getErrorMessage(error: HttpErrorResponse): string {
  if (error.error instanceof ErrorEvent) {
    // Client-side error
    return error.error.message;
  }

  // Server-side error
  if (typeof error.error === 'string') {
    return error.error;
  }

  if (error.error?.message) {
    return error.error.message;
  }

  return error.message || 'Unknown error occurred';
}
